import axios from "axios";
import { throttle } from "lodash";
import store from "../../../../../../redux/store";
import { FUTURES } from "../../../../../../constants";
import {
  BINANCE_STREAM_HOST,
  subscribeBinanceStream,
} from "@helpers/binance/binanceStreams";
import {
  appendLiveTrade,
  getCurrentSecondsBar,
  getSecondsIntervalMs,
} from "./secondsHistory";

const channelToSubscription = new Map();
const REST_KLINE_POLL_MS = 1000;
const WS_STALE_AFTER_MS = 3000;
const CHART_UPDATE_THROTTLE_MS = 100;
const INTERVAL_UNIT_MS = {
  m: 60 * 1000,
  h: 60 * 60 * 1000,
};

/*
 * Тривалість базової свічки Binance в мс.
 * Для 1d / 1w / 1M повертаємо null: нову свічку
 * таких інтервалів відкриває тільки REST-запит.
 */
const getBaseIntervalMs = (klineInterval) => {
  const match = /^(\d+)([mh])$/.exec(klineInterval);

  return match
    ? Number(match[1]) * INTERVAL_UNIT_MS[match[2]]
    : null;
};

const getLatestBaseBar = (subscriptionItem) => {
  let latestBaseBar = null;

  for (const baseBar of subscriptionItem.baseBars.values()) {
    if (!latestBaseBar || baseBar.time > latestBaseBar.time) {
      latestBaseBar = baseBar;
    }
  }

  return latestBaseBar;
};

const notifyBarHandlers = (subscriptionItem, bar) => {
  for (const h of subscriptionItem.handlers) {
    try {
      h.callback(bar);
    } catch {
      /* ignore */
    }
  }
};

const updateChartPrice = (subscriptionItem, bar) => {
  const price = Number(bar?.close);
  const open = Number(bar?.open);

  if (
    !Number.isFinite(price) ||
    !Number.isFinite(open)
  ) {
    return;
  }

  const state = store.getState();
  const activePair = String(
    state?.futures?.pair?.slug || "",
  ).toLowerCase();

  if (
    !activePair ||
    activePair !== subscriptionItem.symbolLower
  ) {
    return;
  }

  const direction = price >= open ? "up" : "down";

  const currentChartPrice = Number(
    state?.futures?.chartPrice,
  );

  const currentChartDirection =
    state?.futures?.chartDirection;

  if (
    Number.isFinite(currentChartPrice) &&
    currentChartPrice === price &&
    currentChartDirection === direction
  ) {
    return;
  }

  store.dispatch({
    type: FUTURES.UPDATE_CHART_PRICE,
    payload: {
      price,
      direction,
    },
    meta: {
      pair: subscriptionItem.symbolLower,
    },
  });
};

const alignedBarOpenTimeMs = (resolution, time) => {
  const r = String(resolution);
  const HOUR_MS = 60 * 60 * 1000;
  const secondsIntervalMs = getSecondsIntervalMs(r);

  if (secondsIntervalMs) {
    return Math.floor(time / secondsIntervalMs) * secondsIntervalMs;
  }

  if (r === "D" || r === "1D") {
    const date = new Date(time);

    return Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
    );
  }

  if (r === "W" || r === "1W") {
    const date = new Date(time);
    const day = date.getUTCDay();
    const daysFromMonday = (day + 6) % 7;

    return Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate() - daysFromMonday,
    );
  }

  if (r === "M" || r === "1M") {
    const date = new Date(time);

    return Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      1,
    );
  }

  if (/^\d+$/.test(r)) {
    const resolutionMs = Number(r) * 60 * 1000;

    return Math.floor(time / resolutionMs) * resolutionMs;
  }

  return Math.floor(time / HOUR_MS) * HOUR_MS;
};

const getResolutionConfig = (resolution) => {
  const r = String(resolution);

  // Секундних kline у Binance немає: свічки будуються тільки з угод
  if (getSecondsIntervalMs(r)) {
    return {
      interval: null,
      restLimit: 0,
    };
  }

  const nativeIntervals = {
    "1": "1m",
    "3": "3m",
    "5": "5m",
    "15": "15m",
    "30": "30m",
    "60": "1h",
    "120": "2h",
    "240": "4h",
    "360": "6h",
    "480": "8h",
    "720": "12h",
    D: "1d",
    "1D": "1d",
    W: "1w",
    "1W": "1w",
    M: "1M",
    "1M": "1M",
  };

  if (nativeIntervals[r]) {
    return {
      interval: nativeIntervals[r],
      restLimit: 2,
    };
  }

  if (/^\d+$/.test(r)) {
    const targetMinutes = Number(r);
    const availableBaseMinutes = [30, 15, 5, 3, 1];

    const baseMinutes =
      availableBaseMinutes.find(
        (minutes) => targetMinutes % minutes === 0,
      ) || 1;

    return {
      interval: `${baseMinutes}m`,
      restLimit: Math.min(
        Math.ceil(targetMinutes / baseMinutes) + 2,
        1500,
      ),
    };
  }

  return {
    interval: "1m",
    restLimit: 2,
  };
};

const normalizeBar = (bar) => {
  const time = Number(bar?.time);
  const open = Number(bar?.open);
  const high = Number(bar?.high);
  const low = Number(bar?.low);
  const close = Number(bar?.close);

  const volume =
    bar?.volume == null || bar.volume === ""
      ? undefined
      : Number(bar.volume);

  if (
    !Number.isFinite(time) ||
    !Number.isFinite(open) ||
    !Number.isFinite(high) ||
    !Number.isFinite(low) ||
    !Number.isFinite(close)
  ) {
    return null;
  }

  return {
    time,
    open,
    high: Math.max(high, open, close),
    low: Math.min(low, open, close),
    close,
    volume: Number.isFinite(volume)
      ? volume
      : undefined,
  };
};

const upsertBaseBar = (subscriptionItem, bar) => {
  const normalizedBar = normalizeBar(bar);

  if (!normalizedBar) return null;

  subscriptionItem.baseBars.set(
    normalizedBar.time,
    normalizedBar,
  );

  const targetTime = alignedBarOpenTimeMs(
    subscriptionItem.resolution,
    normalizedBar.time,
  );

  const bucketBars = Array.from(
    subscriptionItem.baseBars.values(),
  )
    .filter(
      (baseBar) =>
        alignedBarOpenTimeMs(
          subscriptionItem.resolution,
          baseBar.time,
        ) === targetTime,
    )
    .sort((a, b) => a.time - b.time);

  if (!bucketBars.length) return null;

  /*
   * Залишаємо у пам'яті тільки базові свічки,
   * необхідні для поточного або попереднього бару графіка.
   */
  for (
    const [baseTime] of
    subscriptionItem.baseBars.entries()
  ) {
    const baseBucketTime = alignedBarOpenTimeMs(
      subscriptionItem.resolution,
      baseTime,
    );

    if (
      baseBucketTime < targetTime &&
      baseBucketTime !== subscriptionItem.lastBar?.time
    ) {
      subscriptionItem.baseBars.delete(baseTime);
    }
  }

  const firstBar = bucketBars[0];
  const lastBar = bucketBars[bucketBars.length - 1];

  const volumes = bucketBars
    .map(({ volume }) => volume)
    .filter(Number.isFinite);

  return {
    time: targetTime,
    open: firstBar.open,
    high: Math.max(
      ...bucketBars.map(({ high }) => high),
    ),
    low: Math.min(
      ...bucketBars.map(({ low }) => low),
    ),
    close: lastBar.close,
    volume: volumes.length
      ? volumes.reduce(
          (total, volume) => total + volume,
          0,
        )
      : undefined,
  };
};

const emitBar = (subscriptionItem, bar, source) => {
  if (subscriptionItem.isDestroyed) return;

  const normalizedBar = normalizeBar(bar);

  if (!normalizedBar) return;

  const previousBar = subscriptionItem.lastBar;

  /*
   * Графік очікує, що realtime-бари
   * надходитимуть лише у хронологічному порядку.
   */
  if (
    previousBar?.time != null &&
    normalizedBar.time < previousBar.time
  ) {
    return;
  }

  /*
   * Відкладений REST-response не повинен
   * перезаписати свіже WebSocket-оновлення.
   */
  if (
    source === "rest" &&
    subscriptionItem.lastWsMessageAt > 0 &&
    Date.now() -
      subscriptionItem.lastWsMessageAt <
      WS_STALE_AFTER_MS
  ) {
    return;
  }

  let nextBar = normalizedBar;

  if (previousBar?.time === normalizedBar.time) {
    nextBar = {
      ...normalizedBar,
      high: Math.max(
        previousBar.high,
        normalizedBar.high,
      ),
      low: Math.min(
        previousBar.low,
        normalizedBar.low,
      ),
      volume:
        previousBar.volume == null &&
        normalizedBar.volume == null
          ? undefined
          : Math.max(
              previousBar.volume || 0,
              normalizedBar.volume || 0,
            ),
    };
  }

  subscriptionItem.lastBar = nextBar;

  updateChartPrice(
    subscriptionItem,
    nextBar,
  );

  notifyBarHandlers(subscriptionItem, nextBar);
};

/*
 * Тік mark price (раз на секунду) — ціна, за якою бек відкриває і закриває
 * позиції. З нього будуємо OHLC поточної свічки, тож лінія ціни, свічка,
 * ціна у формі й ціна входу — одна й та сама. Об'єм додають угоди окремо.
 */
const applyMarkTick = (subscriptionItem, tick) => {
  const price = Number(tick.p);
  const tickTime = Number(tick.E);

  if (!Number.isFinite(price) || !Number.isFinite(tickTime)) {
    return null;
  }

  const latestBaseBar = getLatestBaseBar(subscriptionItem);
  const { baseIntervalMs, isSecondsResolution } = subscriptionItem;

  // Хвилинні свічки чекають першої свічки з REST (markPriceKlines); секундні відкриваємо самі
  if (!latestBaseBar && !isSecondsResolution) return null;
  if (latestBaseBar && tickTime < latestBaseBar.time) return null;

  if (
    baseIntervalMs &&
    (!latestBaseBar ||
      tickTime >= latestBaseBar.time + baseIntervalMs)
  ) {
    return upsertBaseBar(subscriptionItem, {
      time: Math.floor(tickTime / baseIntervalMs) * baseIntervalMs,
      open: price,
      high: price,
      low: price,
      close: price,
    });
  }

  return upsertBaseBar(subscriptionItem, {
    ...latestBaseBar,
    high: Math.max(latestBaseBar.high, price),
    low: Math.min(latestBaseBar.low, price),
    close: price,
  });
};

/*
 * Угода додає лише об'єм у свічку, яку вже відкрив тік mark price.
 * Ціна угоди (last price) на OHLC не впливає.
 */
const applyTradeVolume = (subscriptionItem, trade) => {
  const quantity = Number(trade.q);
  const tradeTime = Number(trade.T);

  if (!Number.isFinite(quantity) || !Number.isFinite(tradeTime)) {
    return null;
  }

  // Угода вже є в історії, з якої почалась поточна секундна свічка
  if (Number(trade.a) <= subscriptionItem.lastSeededTradeId) return null;

  const latestBaseBar = getLatestBaseBar(subscriptionItem);
  const { baseIntervalMs } = subscriptionItem;

  if (!latestBaseBar || tradeTime < latestBaseBar.time) return null;
  if (
    baseIntervalMs &&
    tradeTime >= latestBaseBar.time + baseIntervalMs
  ) {
    return null;
  }

  return upsertBaseBar(subscriptionItem, {
    ...latestBaseBar,
    volume: (latestBaseBar.volume || 0) + quantity,
  });
};

const clearRestKlineFallback = (
  subscriptionItem,
) => {
  if (subscriptionItem.restPollInterval) {
    clearInterval(
      subscriptionItem.restPollInterval,
    );

    subscriptionItem.restPollInterval = null;
  }
};

const fetchLatestKline = async (
  subscriptionItem,
) => {
  if (
    subscriptionItem.isDestroyed ||
    subscriptionItem.handlers.length === 0 ||
    subscriptionItem.restRequestInFlight
  ) {
    return;
  }

  /*
   * Поки WebSocket регулярно надсилає kline,
   * REST-fallback нічого не запитує.
   */
  if (
    subscriptionItem.lastWsMessageAt > 0 &&
    Date.now() -
      subscriptionItem.lastWsMessageAt <
      WS_STALE_AFTER_MS
  ) {
    return;
  }

  subscriptionItem.restRequestInFlight = true;

  try {
    // Та сама mark-ціна, що й у потоці; об'єм у відповіді нульовий, лишаємо накопичений з угод
    const response = await axios.get(
      "https://fapi.binance.com/fapi/v1/markPriceKlines",
      {
        params: {
          symbol: subscriptionItem.symbolUpper,
          interval:
            subscriptionItem.klineInterval,
          limit: subscriptionItem.restLimit,
        },
      },
    );

    if (subscriptionItem.isDestroyed) return;

    const rows = Array.isArray(response.data)
      ? response.data
      : [];

    if (!rows.length) return;

    let latestAggregatedBar = null;

    rows.forEach((row) => {
      latestAggregatedBar = upsertBaseBar(
        subscriptionItem,
        {
          time: row[0],
          open: row[1],
          high: row[2],
          low: row[3],
          close: row[4],
          volume: subscriptionItem.baseBars.get(Number(row[0]))?.volume,
        },
      );
    });

    if (latestAggregatedBar) {
      emitBar(
        subscriptionItem,
        latestAggregatedBar,
        "rest",
      );
    }
  } catch {
    /*
     * Зберігаємо поточний стан графіка.
     * Наступна polling-ітерація повторить запит.
     */
  } finally {
    subscriptionItem.restRequestInFlight = false;
  }
};

/**
 * REST fallback використовує той самий Binance markPriceKlines endpoint,
 * що й історичні свічки. Він також дає першу свічку після підписки.
 *
 * Realtime-джерелом він стає лише тоді,
 * коли потік mark price не надсилає повідомлення.
 */
const ensureRestKlineFallback = (
  subscriptionItem,
) => {
  // Для секундних свічок kline немає; без потоку вони просто стоять
  if (
    subscriptionItem.restPollInterval ||
    !subscriptionItem.klineInterval
  ) {
    return;
  }

  fetchLatestKline(subscriptionItem);

  subscriptionItem.restPollInterval =
    setInterval(() => {
      fetchLatestKline(subscriptionItem);
    }, REST_KLINE_POLL_MS);
};

/*
 * Кожне повідомлення одразу оновлює свічки в пам'яті
 * (інакше загубимо high/low окремих угод),
 * а графік перемальовуємо не частіше за CHART_UPDATE_THROTTLE_MS.
 * З'єднання і перепідключення — у спільному binanceStreams.
 */
const connectStreams = (subscriptionItem) => {
  if (
    subscriptionItem.isDestroyed ||
    subscriptionItem.unsubscribeStreams
  ) {
    return;
  }

  if (!subscriptionItem.throttledCallback) {
    subscriptionItem.throttledCallback =
      throttle(
        (aggregatedBar) => {
          emitBar(
            subscriptionItem,
            aggregatedBar,
            "ws",
          );
        },
        CHART_UPDATE_THROTTLE_MS,
        {
          leading: true,
          trailing: true,
        },
      );
  }

  // Лише потік цін тримає REST-fallback вимкненим: без тіків свічки не повинні стояти
  const handleBar = (aggregatedBar, isPriceSource) => {
    if (isPriceSource) {
      subscriptionItem.lastWsMessageAt = Date.now();
    }

    if (aggregatedBar) {
      subscriptionItem.throttledCallback(aggregatedBar);
    }
  };

  const { symbolLower } = subscriptionItem;

  const unsubscribers = [
    subscribeBinanceStream(
      BINANCE_STREAM_HOST.MARKET,
      `${symbolLower}@markPrice@1s`,
      (payload) => {
        handleBar(applyMarkTick(subscriptionItem, payload), true);
      },
    ),
    subscribeBinanceStream(
      BINANCE_STREAM_HOST.MARKET,
      `${symbolLower}@aggTrade`,
      (payload) => {
        appendLiveTrade(subscriptionItem.symbolUpper, payload);
        handleBar(applyTradeVolume(subscriptionItem, payload), false);
      },
    ),
  ];

  subscriptionItem.unsubscribeStreams = () => {
    unsubscribers
      .filter(Boolean)
      .forEach((unsubscribe) => unsubscribe());
  };
};

const disconnectStreams = (subscriptionItem) => {
  subscriptionItem.unsubscribeStreams?.();
  subscriptionItem.unsubscribeStreams = null;
};

export const subscribeOnStream = (
  symbolInfo,
  resolution,
  onRealtimeCallback,
  subscriberUID,
  widgetId,
) => {
  const symbol =
    symbolInfo.ticker.toLowerCase();

  const {
    interval: klineInterval,
    restLimit,
  } = getResolutionConfig(resolution);

  /*
   * Додаємо resolution графіка у ключ.
   *
   * Наприклад, 5m і 10m можуть використовувати один
   * Binance base-stream 5m, але це різні підписки.
   */
  const channelString =
    `${symbol}@res_${String(resolution)}` +
    `@kline_${klineInterval}`;

  /*
   * subscriberUID унікальний лише в межах одного datafeed-екземпляра.
   * Додаємо префікс widgetId, щоб кожен handler
   * був унікальним і не витіснявся іншим.
   */
  const handlerId = `${widgetId}:${subscriberUID}`;

  const handler = {
    id: handlerId,
    callback: onRealtimeCallback,
  };

  const existing =
    channelToSubscription.get(
      channelString,
    );

  if (existing) {
    const handlerAlreadyExists =
      existing.handlers.some(
        ({ id }) => id === handlerId,
      );

    if (!handlerAlreadyExists) {
      existing.handlers.push(handler);
    }

    ensureRestKlineFallback(existing);
    connectStreams(existing);

    return;
  }

  const subscriptionItem = {
    handlers: [handler],
    unsubscribeStreams: null,
    throttledCallback: null,
    isDestroyed: false,
    channelString,

    lastBar: null,
    baseBars: new Map(),

    lastWsMessageAt: 0,
    lastSeededTradeId: 0,

    restPollInterval: null,
    restRequestInFlight: false,

    symbolLower: symbol,
    symbolUpper: symbol.toUpperCase(),

    resolution,
    klineInterval,
    baseIntervalMs:
      getSecondsIntervalMs(resolution) ??
      getBaseIntervalMs(klineInterval),
    isSecondsResolution: Boolean(getSecondsIntervalMs(resolution)),
    restLimit,
  };

  // Жива секундна свічка продовжує останню свічку з історії угод (тіки mark price далі рухають її)
  if (subscriptionItem.isSecondsResolution) {
    const seed = getCurrentSecondsBar(
      subscriptionItem.symbolUpper,
      subscriptionItem.baseIntervalMs,
    );

    if (seed?.bar) {
      upsertBaseBar(subscriptionItem, seed.bar);
      subscriptionItem.lastSeededTradeId = seed.lastTradeId;
    }
  }

  channelToSubscription.set(
    channelString,
    subscriptionItem,
  );

  connectStreams(subscriptionItem);
  ensureRestKlineFallback(subscriptionItem);
};

export const unsubscribeFromStream = (
  subscriberUID,
  widgetId,
) => {
  const handlerId = `${widgetId}:${subscriberUID}`;

  for (
    const [
      channelString,
      subscriptionItem,
    ] of channelToSubscription.entries()
  ) {
    const handlerIndex =
      subscriptionItem.handlers.findIndex(
        (handler) =>
          handler.id === handlerId,
      );

    if (handlerIndex === -1) continue;

    subscriptionItem.handlers.splice(
      handlerIndex,
      1,
    );

    if (
      subscriptionItem.handlers.length === 0
    ) {
      subscriptionItem.isDestroyed = true;

      clearRestKlineFallback(
        subscriptionItem,
      );
      disconnectStreams(subscriptionItem);

      subscriptionItem.throttledCallback
        ?.cancel?.();

      subscriptionItem.baseBars.clear();

      channelToSubscription.delete(
        channelString,
      );
    }

    break;
  }
};
